import { createBrowserRouter, Navigate, type RouteObject } from 'react-router'

import { restoreSession } from '../features/auth/api'
import { AuthLayout } from '../features/auth/auth-layout'
import { ForgotPasswordPage } from '../features/auth/forgot-password-page'
import { RequireAnyPermission, RequireAuth, RequirePermission } from '../features/auth/guards'
import { LoginPage } from '../features/auth/login-page'
import { ResetPasswordPage } from '../features/auth/reset-password-page'
import { homePath, NAV_GROUPS, type NavItem } from '../features/shell/nav'
import { useCurrentUser } from '../lib/session'
import { lazyWithRetry } from './lazy-with-retry'
import { LoadingScreen } from './loading-screen'
import { RootLayout } from './root-layout'
import { RouteError } from './route-error'

/** Primeira carga da impressão: nada na tela até a página chegar (sem animação). */
function PrintHydrating() {
  return null
}

/** "/" leva à primeira área do perfil (normalmente /carteira). */
function HomeRedirect() {
  const user = useCurrentUser()
  return <Navigate to={homePath(user)} replace />
}

function navItem(id: string): NavItem & { path: string } {
  const item = NAV_GROUPS.flatMap((group) => group.items).find((candidate) => candidate.id === id)
  if (!item?.path) throw new Error(`Item de navegação sem rota: ${id}`)
  return { ...item, path: item.path }
}

/**
 * Rotas (docs/sprint-0-spec.md §5). A raiz restaura a sessão pelo cookie antes de renderizar
 * (uma vez por carga). As telas de acesso ficam no pacote inicial; o shell e as páginas
 * autenticadas carregam sob demanda (o login não baixa tabela, paleta nem menus), pelo
 * `lazyWithRetry`: rede que oscila ou deploy novo ganham uma segunda tentativa e, se preciso, uma
 * recarga da página antes do "Esta tela não abriu".
 */
export const routes: RouteObject[] = [
  // Páginas de impressão (sprint-10-spec §4): fora da raiz (sem restaurar a sessão pelo cookie),
  // sem shell nem RequireAuth; o token de impressão do fragmento autoriza só as chamadas delas.
  {
    path: 'imprimir',
    errorElement: <RouteError />,
    HydrateFallback: PrintHydrating,
    children: [
      {
        path: 'relatorios/:id',
        lazy: async () => {
          const { PrintReportPage } = await lazyWithRetry(
            'reports/print-report-page',
            () => import('../features/reports/print/print-report-page'),
          )
          return { Component: PrintReportPage }
        },
      },
      {
        path: 'paineis/:id',
        lazy: async () => {
          const { PrintDashboardPage } = await lazyWithRetry(
            'reports/print-dashboard-page',
            () => import('../features/reports/print/print-dashboard-page'),
          )
          return { Component: PrintDashboardPage }
        },
      },
      {
        path: 'painel-executivo',
        lazy: async () => {
          const { PrintExecutivePage } = await lazyWithRetry(
            'dashboard/print-executive-page',
            () => import('../features/dashboard/print/print-executive-page'),
          )
          return { Component: PrintExecutivePage }
        },
      },
    ],
  },
  {
    id: 'root',
    element: <RootLayout />,
    loader: async () => {
      await restoreSession()
      return null
    },
    shouldRevalidate: () => false,
    HydrateFallback: LoadingScreen,
    errorElement: <RouteError />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          { path: 'login', element: <LoginPage /> },
          { path: 'esqueci-senha', element: <ForgotPasswordPage /> },
          { path: 'redefinir-senha/:token', element: <ResetPasswordPage /> },
          {
            path: 'convite/:token',
            lazy: async () => {
              const { InvitePage } = await lazyWithRetry(
                'auth/invite-page',
                () => import('../features/auth/invite-page'),
              )
              return { Component: InvitePage }
            },
          },
        ],
      },
      {
        path: 'trocar-senha',
        lazy: async () => {
          const { ChangePasswordPage } = await lazyWithRetry(
            'auth/change-password-page',
            () => import('../features/auth/change-password-page'),
          )
          return {
            element: (
              <RequireAuth allowPendingPassword>
                <ChangePasswordPage />
              </RequireAuth>
            ),
          }
        },
      },
      {
        // Modo apresentação: tela cheia, fora do shell (sprint-8-spec §3.2).
        path: 'painel/apresentacao',
        lazy: async () => {
          const { PresentationPage } = await lazyWithRetry(
            'dashboard/presentation-page',
            () => import('../features/dashboard/presentation/presentation-page'),
          )
          return {
            element: (
              <RequireAuth>
                <RequirePermission permission="reports.view">
                  <RequirePermission permission="cases.read_all">
                    <PresentationPage />
                  </RequirePermission>
                </RequirePermission>
              </RequireAuth>
            ),
          }
        },
      },
      {
        lazy: async () => {
          const { AppShell } = await lazyWithRetry(
            'shell/app-shell',
            () => import('../features/shell/app-shell'),
          )
          return {
            element: (
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            ),
          }
        },
        children: [
          { index: true, element: <HomeRedirect /> },
          {
            path: navItem('carteira').path.slice(1),
            lazy: async () => {
              const { CarteiraPage } = await lazyWithRetry(
                'cases/carteira-page',
                () => import('../features/cases/carteira-page'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('carteira').permission}>
                    <CarteiraPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'clientes/novo',
            lazy: async () => {
              const { CustomerCreatePage } = await lazyWithRetry(
                'customers/customer-create-page',
                () => import('../features/customers/customer-create-page'),
              )
              return {
                element: (
                  <RequirePermission permission="customers.write">
                    <CustomerCreatePage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'clientes/:id',
            lazy: async () => {
              const { CustomerPage } = await lazyWithRetry(
                'customers/customer-page',
                () => import('../features/customers/customer-page'),
              )
              return {
                element: (
                  <RequirePermission permission="customers.read">
                    <CustomerPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'clientes/:id/acordos/novo',
            lazy: async () => {
              const { SimulatorPage } = await lazyWithRetry(
                'agreements/simulator-page',
                () => import('../features/agreements/simulator-page'),
              )
              return {
                element: (
                  <RequirePermission permission="agreements.write">
                    <SimulatorPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            // Tela Acordos (Q3): os acordos da carteira, com o resumo e as propostas a aprovar.
            path: navItem('acordos').path.slice(1),
            lazy: async () => {
              const { AgreementsPage } = await lazyWithRetry(
                'agreements/agreements-page',
                () => import('../features/agreements/agreements-page'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('acordos').permission}>
                    <AgreementsPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'acordos/:id/proposta',
            lazy: async () => {
              const { ProposalPage } = await lazyWithRetry(
                'agreements/proposal-page',
                () => import('../features/agreements/proposal-page'),
              )
              return {
                element: (
                  <RequirePermission permission="cases.read">
                    <ProposalPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: navItem('fila').path.slice(1),
            lazy: async () => {
              const { QueuePage } = await lazyWithRetry(
                'queue/queue-page',
                () => import('../features/queue/queue-page'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('fila').permission}>
                    <QueuePage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            // Fila em sequência (sprint-14-spec §2.3): a visão de trabalho de um caso por vez.
            path: 'fila/sequencia',
            lazy: async () => {
              const { SequencePage } = await lazyWithRetry(
                'queue/sequence-page',
                () => import('../features/queue/sequence-page'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('fila').permission}>
                    <SequencePage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'preferencias/whatsapp',
            lazy: async () => {
              const { WhatsappPreferencesPage } = await lazyWithRetry(
                'preferences/whatsapp-preferences-page',
                () => import('../features/preferences/whatsapp-preferences-page'),
              )
              return { element: <WhatsappPreferencesPage /> }
            },
          },
          {
            path: navItem('lembretes').path.slice(1),
            lazy: async () => {
              const { RemindersPage } = await lazyWithRetry(
                'reminders/reminders-page',
                () => import('../features/reminders/reminders-page'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('lembretes').permission}>
                    <RemindersPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: navItem('campanhas').path.slice(1),
            lazy: async () => {
              const { CampaignsPage } = await lazyWithRetry(
                'campaigns/campaigns-page',
                () => import('../features/campaigns/campaigns-page'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('campanhas').permission}>
                    <CampaignsPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: `${navItem('campanhas').path.slice(1)}/nova`,
            lazy: async () => {
              const { CampaignNewPage } = await lazyWithRetry(
                'campaigns/campaign-new-page',
                () => import('../features/campaigns/campaign-new-page'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('campanhas').permission}>
                    <CampaignNewPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: `${navItem('campanhas').path.slice(1)}/:id`,
            lazy: async () => {
              const { CampaignDetailPage } = await lazyWithRetry(
                'campaigns/campaign-detail-page',
                () => import('../features/campaigns/campaign-detail-page'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('campanhas').permission}>
                    <CampaignDetailPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: navItem('painel').path.slice(1),
            lazy: async () => {
              const { DashboardPage } = await lazyWithRetry(
                'dashboard/dashboard-page',
                () => import('../features/dashboard/dashboard-page'),
              )
              return {
                element: (
                  <RequirePermission permission="reports.view">
                    <RequirePermission permission="cases.read_all">
                      <DashboardPage />
                    </RequirePermission>
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'painel/meu',
            lazy: async () => {
              const { MeDashboardPage } = await lazyWithRetry(
                'dashboard/me-dashboard-page',
                () => import('../features/dashboard/me-dashboard-page'),
              )
              return {
                element: (
                  <RequirePermission permission="cases.read">
                    <MeDashboardPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: navItem('vendas').path.slice(1),
            lazy: async () => {
              const { SalesPage } = await lazyWithRetry(
                'sales/sales-page',
                () => import('../features/sales/sales-page'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('vendas').permission}>
                    <SalesPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: `${navItem('vendas').path.slice(1)}/leads/:id`,
            lazy: async () => {
              const { LeadPage } = await lazyWithRetry(
                'sales/lead-sheet',
                () => import('../features/sales/lead-sheet'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('vendas').permission}>
                    <LeadPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: navItem('relatorios').path.slice(1),
            lazy: async () => {
              const { LibraryPage } = await lazyWithRetry(
                'reports/library-page',
                () => import('../features/reports/library-page'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('relatorios').permission}>
                    <LibraryPage />
                  </RequirePermission>
                ),
              }
            },
          },
          // Galeria dos 12 gráficos com fixtures sintéticas: só no build do e2e (captura
          // relatorios-graficos-light); o build de produção não tem a rota.
          ...(import.meta.env.VITE_REPORT_GALLERY === 'true'
            ? [
                {
                  path: 'relatorios/galeria',
                  lazy: async () => {
                    const { ReportChartGallery } = await lazyWithRetry(
                      'reports/charts',
                      () => import('../features/reports/charts'),
                    )
                    return {
                      element: (
                        <RequirePermission permission="reports.view">
                          <ReportChartGallery />
                        </RequirePermission>
                      ),
                    }
                  },
                } satisfies RouteObject,
              ]
            : []),
          {
            path: 'relatorios/paineis/novo',
            lazy: async () => {
              const { DashboardEditorPage } = await lazyWithRetry(
                'reports/dashboard-editor-page',
                () => import('../features/reports/dashboards/dashboard-editor-page'),
              )
              return {
                element: (
                  <RequirePermission permission="reports.view">
                    <RequirePermission permission="reports.build">
                      <DashboardEditorPage creating />
                    </RequirePermission>
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'relatorios/paineis/:id',
            lazy: async () => {
              const { DashboardPage } = await lazyWithRetry(
                'reports/dashboard-page',
                () => import('../features/reports/dashboards/dashboard-page'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('relatorios').permission}>
                    <DashboardPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'relatorios/paineis/:id/editar',
            lazy: async () => {
              const { DashboardEditorPage } = await lazyWithRetry(
                'reports/dashboard-editor-page',
                () => import('../features/reports/dashboards/dashboard-editor-page'),
              )
              return {
                element: (
                  <RequirePermission permission="reports.view">
                    <RequirePermission permission="reports.build">
                      <DashboardEditorPage />
                    </RequirePermission>
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'relatorios/novo',
            lazy: async () => {
              const { EditorPage } = await lazyWithRetry(
                'reports/editor-page',
                () => import('../features/reports/editor/editor-page'),
              )
              return {
                element: (
                  <RequirePermission permission="reports.view">
                    <RequirePermission permission="reports.build">
                      <EditorPage creating />
                    </RequirePermission>
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'relatorios/:id',
            lazy: async () => {
              const { ViewPage } = await lazyWithRetry(
                'reports/view-page',
                () => import('../features/reports/view-page'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('relatorios').permission}>
                    <ViewPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'relatorios/:id/editar',
            lazy: async () => {
              const { EditorPage } = await lazyWithRetry(
                'reports/editor-page',
                () => import('../features/reports/editor/editor-page'),
              )
              return {
                element: (
                  <RequirePermission permission="reports.view">
                    <RequirePermission permission="reports.build">
                      <EditorPage />
                    </RequirePermission>
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: navItem('financeiro').path.slice(1),
            lazy: async () => {
              const { OverviewPage } = await lazyWithRetry(
                'finance/overview-page',
                () => import('../features/finance/overview-page'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('financeiro').permission}>
                    <OverviewPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'financeiro/dre',
            lazy: async () => {
              const { DrePage } = await lazyWithRetry(
                'finance/dre-page',
                () => import('../features/finance/dre-page'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('financeiro').permission}>
                    <DrePage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'financeiro/conciliacao',
            lazy: async () => {
              const { ReconciliationPage } = await lazyWithRetry(
                'finance/reconciliation-page',
                () => import('../features/finance/reconciliation-page'),
              )
              return {
                element: (
                  <RequirePermission permission="finance.write">
                    <ReconciliationPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'financeiro/lancamentos',
            lazy: async () => {
              const { LedgerPage } = await lazyWithRetry(
                'finance/ledger-page',
                () => import('../features/finance/ledger-page'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('financeiro').permission}>
                    <LedgerPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: navItem('usuarios').path.slice(1),
            lazy: async () => {
              const { UsersPage } = await lazyWithRetry(
                'users/users-page',
                () => import('../features/users/users-page'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('usuarios').permission}>
                    <UsersPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: navItem('importacoes').path.slice(1),
            lazy: async () => {
              const { ImportsPage } = await lazyWithRetry(
                'imports/imports-page',
                () => import('../features/imports/imports-page'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('importacoes').permission}>
                    <ImportsPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: `${navItem('importacoes').path.slice(1)}/planilha`,
            lazy: async () => {
              const { UniversalImportPage } = await lazyWithRetry(
                'imports/universal-import-page',
                () => import('../features/imports/universal-import-page'),
              )
              return {
                element: (
                  <RequireAnyPermission
                    permissions={[navItem('importacoes').permission, 'sales.manage']}
                  >
                    <UniversalImportPage />
                  </RequireAnyPermission>
                ),
              }
            },
          },
          {
            path: `${navItem('importacoes').path.slice(1)}/mapeamentos`,
            lazy: async () => {
              const { MappingsPage } = await lazyWithRetry(
                'imports/mappings-page',
                () => import('../features/imports/mappings-page'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('importacoes').permission}>
                    <MappingsPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: `${navItem('importacoes').path.slice(1)}/:id`,
            lazy: async () => {
              const { ImportDetailPage } = await lazyWithRetry(
                'imports/import-detail-page',
                () => import('../features/imports/import-detail-page'),
              )
              return {
                element: (
                  <RequireAnyPermission
                    permissions={[navItem('importacoes').permission, 'sales.manage']}
                  >
                    <ImportDetailPage />
                  </RequireAnyPermission>
                ),
              }
            },
          },
          {
            path: `${navItem('duplicidades').path.slice(1)}`,
            lazy: async () => {
              const { DuplicatesPage } = await lazyWithRetry(
                'duplicates/duplicates-page',
                () => import('../features/duplicates/duplicates-page'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('duplicidades').permission}>
                    <DuplicatesPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: `${navItem('duplicidades').path.slice(1)}/mesclagens`,
            lazy: async () => {
              const { MergesPage } = await lazyWithRetry(
                'duplicates/merges-page',
                () => import('../features/duplicates/merges-page'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('duplicidades').permission}>
                    <MergesPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: `${navItem('duplicidades').path.slice(1)}/:id`,
            lazy: async () => {
              const { DuplicateDetailPage } = await lazyWithRetry(
                'duplicates/duplicate-detail-page',
                () => import('../features/duplicates/duplicate-detail-page'),
              )
              return {
                element: (
                  <RequirePermission permission={navItem('duplicidades').permission}>
                    <DuplicateDetailPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'configuracoes',
            lazy: async () => {
              const { SettingsIndex } = await lazyWithRetry(
                'settings/settings-index',
                () => import('../features/settings/settings-index'),
              )
              return { Component: SettingsIndex }
            },
          },
          {
            path: 'configuracoes/perfis',
            lazy: async () => {
              const { RolesPage } = await lazyWithRetry(
                'roles/roles-page',
                () => import('../features/roles/roles-page'),
              )
              return {
                element: (
                  <RequirePermission permission="admin.roles">
                    <RolesPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'configuracoes/cobradoras',
            lazy: async () => {
              const { CollectorsPage } = await lazyWithRetry(
                'collectors/collectors-page',
                () => import('../features/collectors/collectors-page'),
              )
              return {
                element: (
                  <RequirePermission permission="admin.collectors">
                    <CollectorsPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'configuracoes/parametros',
            lazy: async () => {
              const { ParametersPage } = await lazyWithRetry(
                'parameters/parameters-page',
                () => import('../features/parameters/parameters-page'),
              )
              return {
                element: (
                  <RequirePermission permission="admin.settings">
                    <ParametersPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'configuracoes/faixas',
            lazy: async () => {
              const { AgingBandsPage } = await lazyWithRetry(
                'parameters/aging-bands-page',
                () => import('../features/parameters/aging-bands-page'),
              )
              return {
                element: (
                  <RequirePermission permission="admin.settings">
                    <AgingBandsPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'auditoria',
            lazy: async () => {
              const { AuditPage } = await lazyWithRetry(
                'audit/audit-page',
                () => import('../features/audit/audit-page'),
              )
              return {
                element: (
                  <RequirePermission permission="admin.audit">
                    <AuditPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'lgpd',
            lazy: async () => {
              const { LgpdPage } = await lazyWithRetry(
                'lgpd/lgpd-page',
                () => import('../features/lgpd/lgpd-page'),
              )
              // lgpd.manage ou admin.audit (só o registro): a página confere.
              return { element: <LgpdPage /> }
            },
          },
          {
            path: 'lgpd/registro/imprimir',
            lazy: async () => {
              const { RegisterPrintPage } = await lazyWithRetry(
                'lgpd/register-print-page',
                () => import('../features/lgpd/register-print-page'),
              )
              return { element: <RegisterPrintPage /> }
            },
          },
          {
            path: 'configuracoes/dominios',
            lazy: async () => {
              const { LookupsPage } = await lazyWithRetry(
                'lookups/lookups-page',
                () => import('../features/lookups/lookups-page'),
              )
              return {
                element: (
                  <RequirePermission permission="admin.settings">
                    <LookupsPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'configuracoes/regua',
            lazy: async () => {
              const { DunningSettingsPage } = await lazyWithRetry(
                'dunning/dunning-settings-page',
                () => import('../features/dunning/dunning-settings-page'),
              )
              return {
                element: (
                  <RequirePermission permission="admin.settings">
                    <DunningSettingsPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'configuracoes/alertas',
            lazy: async () => {
              const { AlertRulesPage } = await lazyWithRetry(
                'alerts/alert-rules-page',
                () => import('../features/alerts/alert-rules-page'),
              )
              return {
                element: (
                  <RequirePermission permission="admin.settings">
                    <AlertRulesPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'configuracoes/templates',
            lazy: async () => {
              const { TemplatesPage } = await lazyWithRetry(
                'templates/templates-page',
                () => import('../features/templates/templates-page'),
              )
              return {
                element: (
                  <RequirePermission permission="templates.manage">
                    <TemplatesPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'configuracoes/funil-de-vendas',
            lazy: async () => {
              const { PipelineSettingsPage } = await lazyWithRetry(
                'sales/pipeline-settings-page',
                () => import('../features/sales/pipeline-settings-page'),
              )
              return {
                element: (
                  <RequirePermission permission="sales.manage">
                    <PipelineSettingsPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'configuracoes/financeiro',
            lazy: async () => {
              const { FinanceSettingsPage } = await lazyWithRetry(
                'finance/settings/finance-settings-page',
                () => import('../features/finance/settings/finance-settings-page'),
              )
              return {
                element: (
                  <RequirePermission permission="finance.manage">
                    <FinanceSettingsPage />
                  </RequirePermission>
                ),
              }
            },
          },
          {
            path: 'notificacoes',
            lazy: async () => {
              const { NotificationsPage } = await lazyWithRetry(
                'notifications/notifications-page',
                () => import('../features/notifications/notifications-page'),
              )
              return { element: <NotificationsPage /> }
            },
          },
          {
            path: 'preferencias',
            lazy: async () => {
              const { PreferencesPage } = await lazyWithRetry(
                'preferences/preferences-page',
                () => import('../features/preferences/preferences-page'),
              )
              return { element: <PreferencesPage /> }
            },
          },
          {
            path: 'primeiros-passos',
            lazy: async () => {
              const { FirstStepsPage } = await lazyWithRetry(
                'onboarding/first-steps-page',
                () => import('../features/onboarding/first-steps-page'),
              )
              return { Component: FirstStepsPage }
            },
          },
          {
            path: 'perfil',
            lazy: async () => {
              const { ProfilePage } = await lazyWithRetry(
                'profile/profile-page',
                () => import('../features/profile/profile-page'),
              )
              return { Component: ProfilePage }
            },
          },
          {
            path: '*',
            lazy: async () => {
              const { NotFoundPage } = await lazyWithRetry(
                'placeholder/not-found-page',
                () => import('../features/placeholder/not-found-page'),
              )
              return { Component: NotFoundPage }
            },
          },
        ],
      },
    ],
  },
]

/** Router do navegador. Criado no main.tsx (criar já dispara o loader da raiz). */
export function createAppRouter() {
  return createBrowserRouter(routes)
}
